import React from 'react';
import {
  Drawer,
  Typography,
  Box,
  Button,
  IconButton,
  Divider,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Alert,
  Tooltip,
  Grid2,
  FormLabel,
  Switch,
} from '@mui/material';
import {
  XIcon,
  PlusIcon,
  QuestionIcon,
  PencilSimpleIcon,
  TrashSimpleIcon,
} from '@phosphor-icons/react';
import { commonStyles } from '../../../../utils/styles/inputs';
import { createAgentToolApi, updateAgentToolApi } from '../../../../utils/apis';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useAlert } from '../../../../hooks/useAlert';
import { UltravoxToolData } from '../../../../schemas/apis/interface';
import MessagePopup from '../../../common/MessagePopup';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../../utils/redux';
import {
  setToolData,
  resetToolData,
  setErrors,
  setHasAttemptedSave,
  setIsAddingHeader,
  setIsEditingHeader,
  setEditingHeaderIndex,
  setNewHeader,
  resetNewHeader,
  addHeader,
  updateHeader,
  removeHeader,
  setIsAddingParameter,
  setIsEditingParameter,
  setEditingParameterIndex,
  setNewParameter,
  resetNewParameter,
  addParameter,
  updateParameter,
  removeParameter,
  initializeFormForEditing,
} from '../../../../utils/redux/slices/toolsSlice';

// TypeScript interfaces based on Ultravox documentation
interface ToolHeader {
  key: string;
  value: string;
  required: boolean;
}

interface ToolParameter {
  name: string;
  description: string;
  type: 'string' | 'number' | 'integer' | 'boolean' | 'custom';
  location: 'query' | 'header' | 'path' | 'body';
  parameterType: 'dynamic' | 'static' | 'automatic';
  required: boolean;
  knownValue: string;
  value?: string;
  customSchema?: any;
}

interface ToolData {
  name: string;
  description: string;
  toolType: 'HTTP' | 'Client';
  customEndpointUrl: string;
  httpMethod: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  timeoutSeconds: number;
  headers: ToolHeader[];
  parameters: ToolParameter[];
  action: 'default' | 'speaks' | 'listens' | 'speaks_once'; // Update this to match new options
  staticResponse: string;
  precomputable: boolean;
  staticResponseEnabled: boolean; // Add this field
}

interface ToolCreatorProps {
  open: boolean;
  onClose: () => void;
  editingTool?: UltravoxToolData | null;
}

const ToolCreator: React.FC<ToolCreatorProps> = ({
  open,
  onClose,
  editingTool = null,
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { alert, setAlert, showAlert } = useAlert();

  // Get all state from Redux
  const {
    toolData,
    errors,
    hasAttemptedSave,
    isAddingHeader,
    isEditingHeader,
    editingHeaderIndex,
    newHeader,
    isAddingParameter,
    isEditingParameter,
    editingParameterIndex,
    newParameter,
  } = useSelector((state: RootState) => state.tools);

  // Replace useEffect with Redux action
  React.useEffect(() => {
    if (editingTool && open) {
      dispatch(initializeFormForEditing(editingTool));
    } else if (!editingTool && open) {
      dispatch(resetToolData());
    }
  }, [editingTool, open, dispatch]);

  // Update handlers to use Redux actions
  const handleEditHeader = (index: number) => {
    dispatch(setEditingHeaderIndex(index));
    dispatch(setIsEditingHeader(true));
    dispatch(setIsAddingHeader(false));
    dispatch(setNewHeader({ ...toolData.headers[index] }));
  };

  const handleEditParameter = (index: number) => {
    dispatch(setEditingParameterIndex(index));
    dispatch(setIsEditingParameter(true));
    dispatch(setIsAddingParameter(false));
    dispatch(setNewParameter({ ...toolData.parameters[index] }));
  };

  const handleInputChange = (
    field: keyof ToolData,
    value: string | number | boolean
  ) => {
    dispatch(setToolData({ [field]: value }));
  };

  // Add new function for knownValue mapping
  const mapKnownValueToUltravoxEnum = (knownValue: string): string => {
    const knownValueMap: Record<string, string> = {
      'Call ID': 'KNOWN_PARAM_CALL_ID',
      'Call State': 'KNOWN_PARAM_CALL_STATE',
      'Conversation History': 'KNOWN_PARAM_CONVERSATION_HISTORY',
      'Output Sample Rate': 'KNOWN_PARAM_OUTPUT_SAMPLE_RATE',
    };
    return knownValueMap[knownValue] || 'KNOWN_PARAM_UNSPECIFIED';
  };

  // Update the knownValues array to match the documentation
  const knownValues = [
    'Call ID',
    'Call State',
    'Conversation History',
    'Output Sample Rate',
  ] as const;

  // Add the mapping function at the top of the component
  const mapActionToDefaultReaction = (
    action: string
  ):
    | 'AGENT_REACTION_UNSPECIFIED'
    | 'AGENT_REACTION_SPEAKS'
    | 'AGENT_REACTION_LISTENS'
    | 'AGENT_REACTION_SPEAKS_ONCE' => {
    const actionMap: Record<
      string,
      | 'AGENT_REACTION_UNSPECIFIED'
      | 'AGENT_REACTION_SPEAKS'
      | 'AGENT_REACTION_LISTENS'
      | 'AGENT_REACTION_SPEAKS_ONCE'
    > = {
      default: 'AGENT_REACTION_UNSPECIFIED',
      speaks: 'AGENT_REACTION_SPEAKS',
      listens: 'AGENT_REACTION_LISTENS',
      speaks_once: 'AGENT_REACTION_SPEAKS_ONCE',
    };
    return actionMap[action] || 'AGENT_REACTION_UNSPECIFIED';
  };

  const mapLocationToUltravoxEnum = (location: string): string => {
    const locationMap: Record<string, string> = {
      query: 'PARAMETER_LOCATION_QUERY',
      header: 'PARAMETER_LOCATION_HEADER',
      path: 'PARAMETER_LOCATION_PATH',
      body: 'PARAMETER_LOCATION_BODY',
    };
    return locationMap[location] || 'PARAMETER_LOCATION_UNSPECIFIED';
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!toolData.name.trim()) {
      newErrors.name = 'Tool name is required';
    }

    if (!toolData.description.trim()) {
      newErrors.description = 'Tool description is required';
    }

    if (!toolData.customEndpointUrl.trim() && toolData.toolType === 'HTTP') {
      newErrors.customEndpointUrl = 'Endpoint URL is required';
    }

    // Check for required headers
    toolData.headers.forEach((header, index) => {
      if (header.required && (!header.key.trim() || !header.value.trim())) {
        newErrors[`header_${index}`] =
          'Required header must have both key and value';
      }
    });

    // Check for required parameters
    toolData.parameters.forEach((param, index) => {
      if (param.required && !param.name.trim()) {
        newErrors[`param_${index}`] = 'Required parameter must have a name';
      }

      // Validate client tool parameter locations
      if (toolData.toolType === 'Client' && param.location !== 'body') {
        newErrors[`param_location_${index}`] =
          'Client tools only support body parameters';
      }
    });

    dispatch(setErrors(newErrors));
    return Object.keys(newErrors).length === 0;
  };

  // Separate mutations for create and update
  const { mutateAsync: createAgentTool, isPending: isCreating } = useMutation({
    mutationFn: createAgentToolApi,
    onSuccess: () => {
      onClose();
      showAlert('Tool created successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['tools'] });
    },
    onError: (error: any) => {
      showAlert(
        error?.response?.data?.message || 'Failed to create tool',
        'error'
      );
    },
  });

  const { mutateAsync: updateAgentTool, isPending: isUpdating } = useMutation({
    mutationFn: updateAgentToolApi,
    onSuccess: () => {
      onClose();
      showAlert('Tool updated successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['tools'] });
    },
    onError: (error: any) => {
      showAlert(
        error?.response?.data?.message || 'Failed to update tool',
        'error'
      );
    },
  });

  const isPending = isCreating || isUpdating;

  const handleSave = async () => {
    dispatch(setHasAttemptedSave(true));
    if (validateForm()) {
      // Transform the data to match Ultravox API format
      const toolDefinition = {
        modelToolName: toolData.name,
        description: toolData.description,
        timeout: `${toolData.timeoutSeconds}s`,
        ...(toolData.toolType === 'HTTP' && {
          http: {
            baseUrlPattern: toolData.customEndpointUrl,
            httpMethod: toolData.httpMethod.toUpperCase(),
          },
        }),
        ...(toolData.toolType === 'Client' && {
          client: {},
        }),
        dynamicParameters: toolData.parameters
          .filter(p => p.parameterType === 'dynamic')
          .map(p => {
            let schema;

            if (p.type === 'custom') {
              // Handle custom schema - parse the JSON if it exists
              if (p.customSchema && p.customSchema._rawValue) {
                try {
                  schema = JSON.parse(p.customSchema._rawValue);
                  // Add description if it's not already in the custom schema and is provided
                  if (!schema.description && p.description) {
                    schema = { ...schema, description: p.description };
                  }
                } catch {
                  // If parsing fails, fall back to basic schema with description
                  schema = p.description ? { description: p.description } : {};
                }
              } else if (
                p.customSchema &&
                typeof p.customSchema === 'object' &&
                !p.customSchema._rawValue
              ) {
                // If customSchema is already a parsed object (from editing existing tools)
                schema = { ...p.customSchema };
                if (!schema.description && p.description) {
                  schema.description = p.description;
                }
              } else {
                // No custom schema provided, use description only
                schema = p.description ? { description: p.description } : {};
              }
            } else {
              // Standard schema types
              schema = {
                type: p.type,
                description: p.description,
              };
            }

            return {
              name: p.name,
              location: mapLocationToUltravoxEnum(p.location),
              schema,
              required: p.required,
            };
          }),
        staticParameters: [
          // Include headers as static parameters with header location
          ...toolData.headers.map(header => ({
            name: header.key,
            location: 'PARAMETER_LOCATION_HEADER',
            value: header.value,
          })),
          // Include other static parameters (non-header)
          ...toolData.parameters
            .filter(p => p.parameterType === 'static')
            .map(p => ({
              name: p.name,
              location: mapLocationToUltravoxEnum(p.location),
              value: p.value || '',
            })),
        ],
        automaticParameters: toolData.parameters
          .filter(p => p.parameterType === 'automatic')
          .map(p => ({
            name: p.name,
            location: mapLocationToUltravoxEnum(p.location),
            knownValue: mapKnownValueToUltravoxEnum(p.knownValue), // Apply enum mapping
          })),
        requirements: {},
        precomputable: toolData.precomputable,
        // Add the defaultReaction mapping
        defaultReaction: mapActionToDefaultReaction(toolData.action),
        // Add staticResponse mapping
        staticResponse:
          toolData.staticResponseEnabled && toolData.staticResponse
            ? { responseText: toolData.staticResponse }
            : undefined,
      };

      try {
        if (editingTool && editingTool.toolId) {
          // Update existing tool
          await updateAgentTool({
            toolId: editingTool.toolId,
            name: toolData.name,
            definition: toolDefinition,
          });
        } else {
          // Create new tool
          await createAgentTool({
            name: toolData.name,
            definition: toolDefinition,
          });
        }
      } catch (error) {
        // Error handling is done in the mutation's onError callback
        console.error('Tool save error:', error);
      }
    }
  };

  const parameterTypes = [
    { value: 'dynamic', label: 'Dynamic' },
    { value: 'static', label: 'Static' },
    { value: 'automatic', label: 'Automatic' },
  ] as const;

  // Replace the static locations array with a function
  const getAvailableLocations = () => {
    const allLocations = [
      { value: 'query', label: 'Query String' },
      { value: 'header', label: 'Header' },
      { value: 'path', label: 'Path' },
      { value: 'body', label: 'Body' },
    ] as const;

    // For client tools, only body parameters are allowed
    if (toolData.toolType === 'Client') {
      return allLocations.filter(loc => loc.value === 'body');
    }

    // For HTTP tools, all locations are available
    return allLocations;
  };

  // Updated header handlers
  const handleSaveHeader = () => {
    if (!newHeader.key.trim() || !newHeader.value.trim()) {
      return;
    }

    if (isEditingHeader && editingHeaderIndex >= 0) {
      dispatch(updateHeader({ index: editingHeaderIndex, header: newHeader }));
      dispatch(setIsEditingHeader(false));
      dispatch(setEditingHeaderIndex(-1));
    } else if (isAddingHeader) {
      dispatch(addHeader(newHeader));
      dispatch(setIsAddingHeader(false));
    }

    dispatch(resetNewHeader());
  };

  // Updated parameter handlers
  const handleSaveParameter = () => {
    if (!newParameter.name.trim()) {
      return;
    }

    let parameterToSave = { ...newParameter };

    // Validate and process custom schema if it's a custom type
    if (
      newParameter.parameterType === 'dynamic' &&
      newParameter.type === 'custom' &&
      newParameter.customSchema?._rawValue
    ) {
      const rawValue = newParameter.customSchema._rawValue.trim();

      if (rawValue) {
        try {
          const parsedSchema = JSON.parse(rawValue);
          // Update the parameter with parsed schema (remove _rawValue and _isInvalid)
          parameterToSave = {
            ...newParameter,
            customSchema: parsedSchema,
          };
        } catch {
          // Mark as invalid and don't save
          dispatch(
            setNewParameter({
              ...newParameter,
              customSchema: { ...newParameter.customSchema, _isInvalid: true },
            })
          );
          return;
        }
      } else {
        // Empty custom schema is valid, remove it
        parameterToSave = {
          ...newParameter,
          customSchema: undefined,
        };
      }
    }

    if (isEditingParameter && editingParameterIndex >= 0) {
      dispatch(
        updateParameter({
          index: editingParameterIndex,
          parameter: parameterToSave,
        })
      );
      dispatch(setIsEditingParameter(false));
      dispatch(setEditingParameterIndex(-1));
    } else if (isAddingParameter) {
      dispatch(addParameter(parameterToSave));
      dispatch(setIsAddingParameter(false));
    }

    dispatch(resetNewParameter());
  };

  const handleAddHeader = () => {
    dispatch(setIsAddingHeader(true));
    dispatch(setIsEditingHeader(false));
    dispatch(setEditingHeaderIndex(-1));
    dispatch(resetNewHeader());
  };

  const handleCancelHeader = () => {
    dispatch(setIsAddingHeader(false));
    dispatch(setIsEditingHeader(false));
    dispatch(setEditingHeaderIndex(-1));
    dispatch(resetNewHeader());
  };

  const handleAddParameter = () => {
    dispatch(setIsAddingParameter(true));
    dispatch(setIsEditingParameter(false));
    dispatch(setEditingParameterIndex(-1));
    dispatch(resetNewParameter());
  };

  const handleCancelParameter = () => {
    dispatch(setIsAddingParameter(false));
    dispatch(setIsEditingParameter(false));
    dispatch(setEditingParameterIndex(-1));
    dispatch(resetNewParameter());
  };

  // Add helper function for custom schema handling
  const handleCustomSchemaChange = (value: string) => {
    // Always store the raw value for editing, don't validate on every keystroke
    dispatch(
      setNewParameter({
        ...newParameter,
        customSchema: { _rawValue: value, _isInvalid: false },
      })
    );
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{
          paper: {
            sx: {
              width: '637px',
              p: 0,
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#FFFFFF',
            },
          },
        }}
      >
        {/* Sticky Header Section */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: '#FFFFFF',
            px: 2,
            pt: 2,
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            {/* Close (Cancel) Button and Title */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <IconButton
                onClick={onClose}
                size="small"
                sx={{
                  '&:hover': { backgroundColor: '#E8EBF1' },
                  transition: 'all 0.2s ease',
                }}
              >
                <XIcon size={18} />
              </IconButton>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 18,
                  color: 'custom.textBlue',
                  letterSpacing: '-0.2px',
                }}
              >
                {editingTool ? 'Edit Tool' : 'Create New Tool'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* Save Button */}
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={!toolData.name || isPending}
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  borderRadius: '6px',
                  textTransform: 'none',
                  boxShadow: 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                {isPending ? 'Saving...' : 'Save Tool'}
              </Button>
            </Box>
          </Box>

          <Divider
            sx={{
              borderColor: 'rgba(0, 0, 0, 0.08)',
              mb: 1,
              mx: -2,
            }}
          />
        </Box>

        {/* Scrollable Content Area */}
        <Box
          sx={{
            overflow: 'auto',
            flexGrow: 1,
            px: 2,
            pb: 2,
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {/* Details Section */}
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 16,
                color: 'custom.textBlue',
              }}
            >
              Details
            </Typography>

            <Grid2 container spacing={2} sx={{ mb: 1 }}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <FormLabel sx={commonStyles.formLabel}>Tool Type *</FormLabel>
                  <Select
                    value={toolData.toolType}
                    onChange={e =>
                      handleInputChange(
                        'toolType',
                        e.target.value as ToolData['toolType']
                      )
                    }
                    sx={{
                      '& .MuiSelect-select': {
                        fontSize: '14px',
                        fontWeight: 400,
                        background: '#F8F9FC',
                        display: 'flex',
                        padding: '8px 12px',
                        alignItems: 'center',
                        borderRadius: '6px',
                        border: '1px solid #DCE0E7',
                        lineHeight: 'normal',
                        letterSpacing: '-0.14px',
                        color: 'lightGray.text',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  >
                    <MenuItem value="HTTP">HTTP</MenuItem>
                    <MenuItem value="Client">Client</MenuItem>
                  </Select>
                </FormControl>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <FormLabel sx={commonStyles.formLabel}>Tool Name *</FormLabel>
                  <TextField
                    fullWidth
                    value={toolData.name}
                    onChange={e => handleInputChange('name', e.target.value)}
                    error={hasAttemptedSave && !!errors.name}
                    helperText={hasAttemptedSave ? errors.name : ''}
                    placeholder="Enter tool name"
                    sx={commonStyles.textField}
                  />
                </FormControl>
              </Grid2>
            </Grid2>

            <FormControl fullWidth size="small">
              <FormLabel sx={commonStyles.formLabel}>Description</FormLabel>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={toolData.description}
                onChange={e => handleInputChange('description', e.target.value)}
                error={hasAttemptedSave && !!errors.description}
                helperText={hasAttemptedSave ? errors.description : ''}
                placeholder="Enter tool description"
                sx={{
                  ...commonStyles.textField,
                  '& .MuiOutlinedInput-root': {
                    ...commonStyles.textField['& .MuiOutlinedInput-root'],
                    height: 'auto',
                    minHeight: '40px',
                  },
                }}
              />
            </FormControl>

            {/* Integration Section */}
            {toolData.toolType === 'HTTP' && (
              <>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: 16,
                    color: 'custom.textBlue',
                    mt: 1,
                  }}
                >
                  Integration
                </Typography>

                <FormControl fullWidth size="small">
                  <FormLabel sx={commonStyles.formLabel}>
                    Custom Endpoint URL *
                  </FormLabel>
                  <TextField
                    fullWidth
                    value={toolData.customEndpointUrl}
                    onChange={e =>
                      handleInputChange('customEndpointUrl', e.target.value)
                    }
                    error={hasAttemptedSave && !!errors.customEndpointUrl}
                    helperText={
                      hasAttemptedSave ? errors.customEndpointUrl : ''
                    }
                    placeholder="Enter endpoint URL"
                    sx={commonStyles.textField}
                  />
                </FormControl>

                <Grid2 container spacing={2} sx={{ mb: 2 }}>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <FormLabel sx={commonStyles.formLabel}>Type *</FormLabel>
                      <Select
                        value={toolData.httpMethod}
                        onChange={e =>
                          handleInputChange(
                            'httpMethod',
                            e.target.value as ToolData['httpMethod']
                          )
                        }
                        sx={{
                          '& .MuiSelect-select': {
                            fontSize: '14px',
                            fontWeight: 400,
                            background: '#F8F9FC',
                            display: 'flex',
                            padding: '8px 12px',
                            alignItems: 'center',
                            borderRadius: '6px',
                            border: '1px solid #DCE0E7',
                            lineHeight: 'normal',
                            letterSpacing: '-0.14px',
                            color: 'lightGray.text',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          },
                        }}
                      >
                        <MenuItem value="GET">GET</MenuItem>
                        <MenuItem value="POST">POST</MenuItem>
                        <MenuItem value="PUT">PUT</MenuItem>
                        <MenuItem value="DELETE">DELETE</MenuItem>
                        <MenuItem value="PATCH">PATCH</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <FormLabel sx={commonStyles.formLabel}>
                        Timeout Time (seconds) *
                      </FormLabel>
                      <TextField
                        fullWidth
                        type="number"
                        value={toolData.timeoutSeconds}
                        onChange={e =>
                          handleInputChange(
                            'timeoutSeconds',
                            parseFloat(e.target.value)
                          )
                        }
                        inputProps={{ step: 0.1, min: 0.1 }}
                        placeholder="Enter timeout"
                        sx={commonStyles.textField}
                      />
                    </FormControl>
                  </Grid2>
                </Grid2>

                {/* Precomputable Section */}
                <Box
                  sx={{
                    mt: 1,
                    mb: 2,
                    p: 2,
                    border: '1px solid #DCE0E7',
                    borderRadius: '8px',
                    bgcolor: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#2B2D3F',
                      }}
                    >
                      Precomputable
                    </Typography>
                    <Tooltip
                      title="When enabled, the tool can be precomputed before the call starts, potentially improving performance."
                      placement="top"
                    >
                      <IconButton size="small" sx={{ padding: '2px' }}>
                        <QuestionIcon size={14} color="#6B6F80" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Switch
                    checked={toolData.precomputable}
                    onChange={e =>
                      handleInputChange('precomputable', e.target.checked)
                    }
                    color="primary"
                    size="small"
                    sx={{
                      width: 36,
                      height: 20,
                      padding: 0,
                      '& .MuiSwitch-switchBase': {
                        padding: 0,
                        margin: '2px',
                        transitionDuration: '300ms',
                        '&.Mui-checked': {
                          transform: 'translateX(16px)',
                          color: '#fff',
                          '& + .MuiSwitch-track': {
                            backgroundColor: '#6366F1',
                            opacity: 1,
                            border: 0,
                          },
                        },
                        '&.Mui-focusVisible .MuiSwitch-thumb': {
                          color: '#6366F1',
                          border: '6px solid #fff',
                        },
                      },
                      '& .MuiSwitch-thumb': {
                        boxSizing: 'border-box',
                        width: 16,
                        height: 16,
                      },
                      '& .MuiSwitch-track': {
                        borderRadius: 26 / 2,
                        backgroundColor: '#E9E9EA',
                        opacity: 1,
                      },
                    }}
                  />
                </Box>

                {/* Headers Section - Updated with similar UI structure */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: '14px',
                          color: '#302B3F',
                        }}
                      >
                        Headers
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 400,
                          fontSize: '12px',
                          color: '#6B6F80',
                        }}
                      >
                        Enter any Key/Value pairs here
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<PlusIcon size={16} />}
                      onClick={handleAddHeader}
                      disabled={isAddingHeader || isEditingHeader}
                      sx={{
                        fontSize: 14,
                        width: 'fit-content',
                        height: '34px',
                        fontWeight: 500,
                        borderRadius: '6px',
                        textTransform: 'none',
                        boxShadow: 'none',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                        },
                      }}
                    >
                      Add Header
                    </Button>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      gap: 2,
                      backgroundColor: '#FFFFFF',
                    }}
                  >
                    {isAddingHeader || isEditingHeader ? (
                      // Header Form
                      <Box
                        sx={{
                          backgroundColor: '#F8F8F8',
                          borderRadius: '6px',
                          padding: '16px',
                          width: '100%',
                          border: '1px solid #DCE0E7',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            gap: 2,
                            '& > div': {
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              height: '40px',
                            },
                            '& .MuiTypography-root': {
                              width: '114px',
                              fontWeight: 400,
                              fontSize: '12px',
                              color: '#6B6F80',
                            },
                          }}
                        >
                          {/* Key field */}
                          <Box>
                            <Typography>Key</Typography>
                            <TextField
                              value={newHeader.key}
                              onChange={e =>
                                dispatch(
                                  setNewHeader({
                                    ...newHeader,
                                    key: e.target.value,
                                  })
                                )
                              }
                              placeholder="Content-Type"
                              sx={{
                                width: '459px',
                                height: '40px',
                                '& .MuiOutlinedInput-root': {
                                  height: '40px',
                                  borderRadius: '6px',
                                  backgroundColor: '#FFFFFF',
                                  '& fieldset': {
                                    borderWidth: '1px',
                                    borderColor: '#DCE0E7',
                                    '&:hover': {
                                      borderColor: '#DCE0E7',
                                    },
                                  },
                                },
                                '& .MuiOutlinedInput-input': {
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  color: '#302B3F',
                                  backgroundColor: '#FFFFFF',
                                  '&::placeholder': {
                                    color: '#6B6F80',
                                  },
                                },
                              }}
                            />
                          </Box>

                          {/* Value field */}
                          <Box>
                            <Typography>Value</Typography>
                            <TextField
                              value={newHeader.value}
                              onChange={e =>
                                dispatch(
                                  setNewHeader({
                                    ...newHeader,
                                    value: e.target.value,
                                  })
                                )
                              }
                              placeholder="application/json"
                              fullWidth
                              sx={{
                                width: '459px',
                                height: '40px',
                                '& .MuiOutlinedInput-root': {
                                  height: '40px',
                                  borderRadius: '6px',
                                  backgroundColor: '#FFFFFF',
                                  '& fieldset': {
                                    borderWidth: '1px',
                                    borderColor: '#DCE0E7',
                                    '&:hover': {
                                      borderColor: '#DCE0E7',
                                    },
                                  },
                                },
                                '& .MuiOutlinedInput-input': {
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  color: '#302B3F',
                                  backgroundColor: '#FFFFFF',
                                  '&::placeholder': {
                                    color: '#6B6F80',
                                  },
                                },
                              }}
                            />
                          </Box>

                          {/* Cancel & Save Buttons */}
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                              gap: 2,
                              marginTop: 1,
                              height: 'auto !important',
                            }}
                          >
                            <Button
                              onClick={handleCancelHeader}
                              sx={{
                                fontSize: 14,
                                fontWeight: 500,
                                textTransform: 'none',
                                color: '#6B6F80',
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                  color: '#302B3F',
                                },
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              onClick={handleSaveHeader}
                              disabled={!newHeader.key || !newHeader.value}
                              sx={{
                                fontSize: 14,
                                fontWeight: 500,
                                textTransform: 'none',
                                backgroundColor: '#6366F1',
                                borderRadius: '6px',
                                '&:hover': {
                                  backgroundColor: '#4F46E5',
                                },
                                '&.Mui-disabled': {
                                  backgroundColor: '#E4E7EC',
                                  color: '#9DA4AE',
                                },
                              }}
                            >
                              {isEditingHeader
                                ? 'Update Header'
                                : 'Save Header'}
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    ) : toolData.headers.filter(
                        header => header.key.trim() || header.value.trim()
                      ).length > 0 ? (
                      // Display Headers List - only show headers that have content
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                          backgroundColor: '#F8F8F8',
                          padding: '12px 16px',
                          borderRadius: '6px',
                          border: '1px solid #DCE0E7',
                        }}
                      >
                        {toolData.headers
                          .filter(
                            header => header.key.trim() || header.value.trim()
                          )
                          .map((header, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: 'flex',
                                height: '40px',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '12px 16px',
                                backgroundColor: '#FFFFFF',
                                borderRadius: '8px',
                                border: '1px solid #DCE0E7',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 2,
                                  alignItems: 'center',
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 400,
                                    fontSize: '12px',
                                    color: '#6B6F80',
                                  }}
                                >
                                  Header
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: '14px',
                                      color: '#302B3F',
                                    }}
                                  >
                                    {header.key}: {header.value}
                                  </Typography>
                                  {header.required && (
                                    <Tooltip title="Required">
                                      <Typography
                                        sx={{
                                          color: '#EF4444',
                                          fontSize: '14px',
                                          marginLeft: '2px',
                                        }}
                                      >
                                        *
                                      </Typography>
                                    </Tooltip>
                                  )}
                                </Box>
                              </Box>
                              <Box
                                sx={{ display: 'flex', alignItems: 'center' }}
                              >
                                <IconButton
                                  onClick={() => handleEditHeader(index)}
                                >
                                  <PencilSimpleIcon size={16} />
                                </IconButton>
                                <IconButton
                                  onClick={() => dispatch(removeHeader(index))}
                                >
                                  <TrashSimpleIcon
                                    size={16}
                                    weight="fill"
                                    color="#E74C3C"
                                  />
                                </IconButton>
                              </Box>
                            </Box>
                          ))}
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          backgroundColor: '#F8F8F8',
                          borderRadius: '6px',
                          padding: '16px',
                          width: '100%',
                          textAlign: 'center',
                          color: '#6B6F80',
                          fontSize: '12px',
                          fontWeight: 400,
                        }}
                      >
                        No headers added. Click "Add Header" to add your first
                        header
                      </Box>
                    )}
                  </Box>
                </Box>
              </>
            )}

            {/* Parameters Section - Updated with similar UI structure */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: '14px',
                      color: '#302B3F',
                    }}
                  >
                    Parameters
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: '12px',
                      color: '#6B6F80',
                    }}
                  >
                    Define the parameters your tool accepts
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<PlusIcon size={16} />}
                  onClick={handleAddParameter}
                  disabled={isAddingParameter || isEditingParameter}
                  sx={{
                    fontSize: 14,
                    width: 'fit-content',
                    height: '34px',
                    fontWeight: 500,
                    borderRadius: '6px',
                    textTransform: 'none',
                    boxShadow: 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                    },
                  }}
                >
                  Add Parameter
                </Button>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  backgroundColor: '#FFFFFF',
                }}
              >
                {isAddingParameter || isEditingParameter ? (
                  // Parameter Form
                  <Box
                    sx={{
                      backgroundColor: '#F8F8F8',
                      borderRadius: '6px',
                      padding: '16px',
                      width: '100%',
                      border: '1px solid #DCE0E7',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        gap: 2,
                        '& > div': {
                          display: 'flex',
                          alignItems: 'flex-start', // Changed from 'center' to 'flex-start'
                          gap: 2,
                          minHeight: '40px', // Changed from height to minHeight
                        },
                        '& .MuiTypography-root': {
                          width: '114px',
                          fontWeight: 400,
                          fontSize: '12px',
                          color: '#6B6F80',
                          flexShrink: 0, // Prevent label from shrinking
                        },
                      }}
                    >
                      {/* Name field - always visible */}
                      <Box>
                        <Typography>Name</Typography>
                        <TextField
                          value={newParameter.name}
                          onChange={e =>
                            dispatch(
                              setNewParameter({
                                ...newParameter,
                                name: e.target.value,
                              })
                            )
                          }
                          placeholder="Parameter name"
                          sx={{
                            width: '459px',
                            height: '40px',
                            '& .MuiOutlinedInput-root': {
                              height: '40px',
                              borderRadius: '6px',
                              backgroundColor: '#FFFFFF',
                              '& fieldset': {
                                borderWidth: '1px',
                                borderColor: '#DCE0E7',
                                '&:hover': {
                                  borderColor: '#DCE0E7',
                                },
                              },
                            },
                            '& .MuiOutlinedInput-input': {
                              padding: '8px 12px',
                              fontSize: '14px',
                              color: '#302B3F',
                              backgroundColor: '#FFFFFF',
                              '&::placeholder': {
                                color: '#6B6F80',
                              },
                            },
                          }}
                        />
                      </Box>

                      {/* Parameter Type field - always visible */}
                      <Box>
                        <Typography>Parameter Type</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {parameterTypes.map(type => (
                            <Chip
                              key={type.value}
                              label={type.label}
                              onClick={() =>
                                dispatch(
                                  setNewParameter({
                                    ...newParameter,
                                    parameterType: type.value,
                                  })
                                )
                              }
                              color={
                                newParameter.parameterType === type.value
                                  ? 'primary'
                                  : 'default'
                              }
                              variant={
                                newParameter.parameterType === type.value
                                  ? 'filled'
                                  : 'outlined'
                              }
                              size="small"
                              sx={{
                                fontSize: 12,
                                height: 28,
                              }}
                            />
                          ))}
                        </Box>
                      </Box>

                      {/* Description and Type fields - only for dynamic */}
                      {newParameter.parameterType === 'dynamic' && (
                        <>
                          <Box>
                            <Typography>Type</Typography>
                            <Select
                              value={newParameter.type}
                              onChange={e =>
                                dispatch(
                                  setNewParameter({
                                    ...newParameter,
                                    type: e.target
                                      .value as ToolParameter['type'],
                                  })
                                )
                              }
                              sx={{
                                width: '459px',
                                height: '40px',
                                '& .MuiOutlinedInput-root': {
                                  height: '40px',
                                  borderRadius: '6px',
                                  backgroundColor: '#FFFFFF',
                                  '& fieldset': {
                                    borderWidth: '1px',
                                    borderColor: '#DCE0E7',
                                    '&:hover': {
                                      borderColor: '#DCE0E7',
                                    },
                                  },
                                },
                                '& .MuiSelect-select': {
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  color: '#302B3F',
                                  backgroundColor: '#FFFFFF',
                                },
                              }}
                            >
                              <MenuItem value="string">string</MenuItem>
                              <MenuItem value="number">number</MenuItem>
                              <MenuItem value="integer">integer</MenuItem>
                              <MenuItem value="boolean">boolean</MenuItem>
                              <MenuItem value="custom">Custom</MenuItem>
                            </Select>
                          </Box>

                          <Box>
                            <Typography>Description</Typography>
                            <TextField
                              value={newParameter.description}
                              onChange={e =>
                                dispatch(
                                  setNewParameter({
                                    ...newParameter,
                                    description: e.target.value,
                                  })
                                )
                              }
                              placeholder="Parameter description"
                              fullWidth
                              sx={{
                                width: '459px',
                                height: '40px',
                                '& .MuiOutlinedInput-root': {
                                  height: '40px',
                                  borderRadius: '6px',
                                  backgroundColor: '#FFFFFF',
                                  '& fieldset': {
                                    borderWidth: '1px',
                                    borderColor: '#DCE0E7',
                                    '&:hover': {
                                      borderColor: '#DCE0E7',
                                    },
                                  },
                                },
                                '& .MuiOutlinedInput-input': {
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  color: '#302B3F',
                                  backgroundColor: '#FFFFFF',
                                  '&::placeholder': {
                                    color: '#6B6F80',
                                  },
                                },
                              }}
                            />
                          </Box>
                        </>
                      )}

                      {/* Additional fields based on parameter type */}
                      {newParameter.parameterType === 'dynamic' && (
                        <>
                          <Box>
                            <Typography>Location</Typography>
                            <Select
                              value={newParameter.location}
                              onChange={e =>
                                dispatch(
                                  setNewParameter({
                                    ...newParameter,
                                    location: e.target
                                      .value as ToolParameter['location'],
                                  })
                                )
                              }
                              sx={{
                                width: '459px',
                                height: '40px',
                                '& .MuiOutlinedInput-root': {
                                  height: '40px',
                                  borderRadius: '6px',
                                  backgroundColor: '#FFFFFF',
                                  '& fieldset': {
                                    borderWidth: '1px',
                                    borderColor: '#DCE0E7',
                                    '&:hover': {
                                      borderColor: '#DCE0E7',
                                    },
                                  },
                                },
                                '& .MuiSelect-select': {
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  color: '#302B3F',
                                  backgroundColor: '#FFFFFF',
                                },
                              }}
                            >
                              {getAvailableLocations().map(loc => (
                                <MenuItem key={loc.value} value={loc.value}>
                                  {loc.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </Box>

                          <Box>
                            <Typography>Required</Typography>
                            <Select
                              value={newParameter.required ? 'yes' : 'no'}
                              onChange={e =>
                                dispatch(
                                  setNewParameter({
                                    ...newParameter,
                                    required: e.target.value === 'yes',
                                  })
                                )
                              }
                              sx={{
                                width: '459px',
                                height: '40px',
                                '& .MuiOutlinedInput-root': {
                                  height: '40px',
                                  borderRadius: '6px',
                                  backgroundColor: '#FFFFFF',
                                  '& fieldset': {
                                    borderWidth: '1px',
                                    borderColor: '#DCE0E7',
                                    '&:hover': {
                                      borderColor: '#DCE0E7',
                                    },
                                  },
                                },
                                '& .MuiSelect-select': {
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  color: '#302B3F',
                                  backgroundColor: '#FFFFFF',
                                },
                              }}
                            >
                              <MenuItem value="no">No</MenuItem>
                              <MenuItem value="yes">Yes</MenuItem>
                            </Select>
                          </Box>
                        </>
                      )}

                      {newParameter.parameterType === 'static' && (
                        <>
                          <Box>
                            <Typography>Location</Typography>
                            <Select
                              value={newParameter.location}
                              onChange={e =>
                                dispatch(
                                  setNewParameter({
                                    ...newParameter,
                                    location: e.target
                                      .value as ToolParameter['location'],
                                  })
                                )
                              }
                              sx={{
                                width: '459px',
                                height: '40px',
                                '& .MuiOutlinedInput-root': {
                                  height: '40px',
                                  borderRadius: '6px',
                                  backgroundColor: '#FFFFFF',
                                  '& fieldset': {
                                    borderWidth: '1px',
                                    borderColor: '#DCE0E7',
                                    '&:hover': {
                                      borderColor: '#DCE0E7',
                                    },
                                  },
                                },
                                '& .MuiSelect-select': {
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  color: '#302B3F',
                                  backgroundColor: '#FFFFFF',
                                },
                              }}
                            >
                              {getAvailableLocations().map(loc => (
                                <MenuItem key={loc.value} value={loc.value}>
                                  {loc.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </Box>

                          <Box>
                            <Typography>Value</Typography>
                            <TextField
                              value={newParameter.value || ''}
                              onChange={e =>
                                dispatch(
                                  setNewParameter({
                                    ...newParameter,
                                    value: e.target.value,
                                  })
                                )
                              }
                              placeholder="Enter static value"
                              fullWidth
                              sx={{
                                width: '459px',
                                height: '40px',
                                '& .MuiOutlinedInput-root': {
                                  height: '40px',
                                  borderRadius: '6px',
                                  backgroundColor: '#FFFFFF',
                                  '& fieldset': {
                                    borderWidth: '1px',
                                    borderColor: '#DCE0E7',
                                    '&:hover': {
                                      borderColor: '#DCE0E7',
                                    },
                                  },
                                },
                                '& .MuiOutlinedInput-input': {
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  color: '#302B3F',
                                  backgroundColor: '#FFFFFF',
                                  '&::placeholder': {
                                    color: '#6B6F80',
                                  },
                                },
                              }}
                            />
                          </Box>
                        </>
                      )}

                      {newParameter.parameterType === 'automatic' && (
                        <>
                          <Box>
                            <Typography>Location</Typography>
                            <Select
                              value={newParameter.location}
                              onChange={e =>
                                dispatch(
                                  setNewParameter({
                                    ...newParameter,
                                    location: e.target
                                      .value as ToolParameter['location'],
                                  })
                                )
                              }
                              sx={{
                                width: '459px',
                                height: '40px',
                                '& .MuiOutlinedInput-root': {
                                  height: '40px',
                                  borderRadius: '6px',
                                  backgroundColor: '#FFFFFF',
                                  '& fieldset': {
                                    borderWidth: '1px',
                                    borderColor: '#DCE0E7',
                                    '&:hover': {
                                      borderColor: '#DCE0E7',
                                    },
                                  },
                                },
                                '& .MuiSelect-select': {
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  color: '#302B3F',
                                  backgroundColor: '#FFFFFF',
                                },
                              }}
                            >
                              {getAvailableLocations().map(loc => (
                                <MenuItem key={loc.value} value={loc.value}>
                                  {loc.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </Box>

                          <Box>
                            <Typography>Known Value</Typography>
                            <Select
                              value={newParameter.knownValue}
                              onChange={e =>
                                dispatch(
                                  setNewParameter({
                                    ...newParameter,
                                    knownValue: e.target.value,
                                  })
                                )
                              }
                              sx={{
                                width: '459px',
                                height: '40px',
                                '& .MuiOutlinedInput-root': {
                                  height: '40px',
                                  borderRadius: '6px',
                                  backgroundColor: '#FFFFFF',
                                  '& fieldset': {
                                    borderWidth: '1px',
                                    borderColor: '#DCE0E7',
                                    '&:hover': {
                                      borderColor: '#DCE0E7',
                                    },
                                  },
                                },
                                '& .MuiSelect-select': {
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  color: '#302B3F',
                                  backgroundColor: '#FFFFFF',
                                },
                              }}
                            >
                              <MenuItem value="">Select a Known Value</MenuItem>
                              {knownValues.map(value => (
                                <MenuItem key={value} value={value}>
                                  {value}
                                </MenuItem>
                              ))}
                            </Select>
                          </Box>
                        </>
                      )}

                      {/* Custom Schema Editor - only for dynamic custom type */}
                      {newParameter.parameterType === 'dynamic' &&
                        newParameter.type === 'custom' && (
                          <Box>
                            <Typography>Custom Schema</Typography>
                            <TextField
                              value={newParameter.customSchema?._rawValue || ''}
                              onChange={e =>
                                handleCustomSchemaChange(e.target.value)
                              }
                              placeholder='{"type": "string", "enum": ["option1", "option2"]} or {"type": "object", "properties": {...}}'
                              multiline
                              rows={6}
                              error={!!newParameter.customSchema?._isInvalid}
                              helperText={
                                newParameter.customSchema?._isInvalid
                                  ? 'Invalid JSON format. Please enter valid JSON.'
                                  : 'Enter valid JSON schema definition'
                              }
                              sx={{
                                width: '459px',
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '6px',
                                  backgroundColor: '#FFFFFF',
                                  fontFamily: 'monospace',
                                  fontSize: '13px',
                                  '& fieldset': {
                                    borderWidth: '1px',
                                    borderColor: newParameter.customSchema
                                      ?._isInvalid
                                      ? '#f44336'
                                      : '#DCE0E7',
                                  },
                                },
                                '& .MuiInputBase-input': {
                                  fontFamily: 'monospace',
                                  fontSize: '13px',
                                  lineHeight: '1.4',
                                },
                              }}
                            />
                          </Box>
                        )}

                      {/* Cancel & Save Buttons */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: 2,
                          marginTop: 1,
                          height: 'auto !important',
                        }}
                      >
                        <Button
                          onClick={handleCancelParameter}
                          sx={{
                            fontSize: 14,
                            fontWeight: 500,
                            textTransform: 'none',
                            color: '#6B6F80',
                            '&:hover': {
                              backgroundColor: 'transparent',
                              color: '#302B3F',
                            },
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleSaveParameter}
                          disabled={!newParameter.name}
                          sx={{
                            fontSize: 14,
                            fontWeight: 500,
                            textTransform: 'none',
                            backgroundColor: '#6366F1',
                            borderRadius: '6px',
                            '&:hover': {
                              backgroundColor: '#4F46E5',
                            },
                            '&.Mui-disabled': {
                              backgroundColor: '#E4E7EC',
                              color: '#9DA4AE',
                            },
                          }}
                        >
                          {isEditingParameter
                            ? 'Update Parameter'
                            : 'Save Parameter'}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                ) : toolData.parameters.filter(param => param.name.trim())
                    .length > 0 ? (
                  // Display Parameters List - only show parameters that have content
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      backgroundColor: '#F8F8F8',
                      padding: '12px 16px',
                      borderRadius: '6px',
                      border: '1px solid #DCE0E7',
                    }}
                  >
                    {toolData.parameters
                      .filter(param => param.name.trim())
                      .map((param, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            height: '40px',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '12px 16px',
                            backgroundColor: '#FFFFFF',
                            borderRadius: '8px',
                            border: '1px solid #DCE0E7',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 2,
                              alignItems: 'center',
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 400,
                                fontSize: '12px',
                                color: '#6B6F80',
                              }}
                            >
                              {param.parameterType}
                            </Typography>
                            <Box
                              sx={{ display: 'flex', alignItems: 'flex-start' }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 500,
                                  fontSize: '14px',
                                  color: '#302B3F',
                                }}
                              >
                                {param.name}
                              </Typography>
                              {param.required && (
                                <Tooltip title="Required">
                                  <Typography
                                    sx={{
                                      color: '#EF4444',
                                      fontSize: '14px',
                                      marginLeft: '2px',
                                    }}
                                  >
                                    *
                                  </Typography>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton
                              onClick={() => handleEditParameter(index)}
                            >
                              <PencilSimpleIcon size={16} />
                            </IconButton>
                            <IconButton
                              onClick={() => dispatch(removeParameter(index))}
                            >
                              <TrashSimpleIcon
                                size={16}
                                weight="fill"
                                color="#E74C3C"
                              />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      backgroundColor: '#F8F8F8',
                      borderRadius: '6px',
                      padding: '16px',
                      width: '100%',
                      textAlign: 'center',
                      color: '#6B6F80',
                      fontSize: '12px',
                      fontWeight: 400,
                    }}
                  >
                    No parameters added. Click "Add Parameter" to add your first
                    parameter
                  </Box>
                )}
              </Box>
            </Box>

            {/* Optional Configurations Section - Updated with similar UI structure */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: '14px',
                      color: '#302B3F',
                    }}
                  >
                    Optional Configurations
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: '12px',
                      color: '#6B6F80',
                    }}
                  >
                    Configure additional settings for your tool
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  backgroundColor: '#F8F8F8',
                  borderRadius: '6px',
                  padding: '16px 8px',
                }}
              >
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <FormLabel sx={commonStyles.formLabel}>
                        Action
                        <Tooltip title="Defines how the agent reacts after calling this tool">
                          <QuestionIcon
                            style={{ marginLeft: 4, fontSize: 14 }}
                            size={14}
                          />
                        </Tooltip>
                      </FormLabel>
                      <Select
                        value={toolData.action}
                        onChange={e =>
                          handleInputChange(
                            'action',
                            e.target.value as ToolData['action']
                          )
                        }
                        sx={{
                          '& .MuiSelect-select': {
                            fontSize: '14px',
                            fontWeight: 400,
                            background: '#F8F9FC',
                            display: 'flex',
                            padding: '8px 12px',
                            alignItems: 'center',
                            borderRadius: '6px',
                            border: '1px solid #DCE0E7',
                            lineHeight: 'normal',
                            letterSpacing: '-0.14px',
                            color: 'lightGray.text',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          },
                        }}
                      >
                        <MenuItem value="default">Default</MenuItem>
                        <MenuItem value="speaks">Speaks</MenuItem>
                        <MenuItem value="listens">Listens</MenuItem>
                        <MenuItem value="speaks_once">Speaks Once</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid2>

                  <Grid2 size={{ xs: 12 }}>
                    <Box
                      sx={{
                        mt: 1,
                        mb: 2,
                        p: 2,
                        border: '1px solid #DCE0E7',
                        borderRadius: '8px',
                        bgcolor: '#FFFFFF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#2B2D3F',
                          }}
                        >
                          Static Response
                        </Typography>
                        <Tooltip
                          title="Enable to provide a static response that the agent will use"
                          placement="top"
                        >
                          <IconButton size="small" sx={{ padding: '2px' }}>
                            <QuestionIcon size={14} color="#6B6F80" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Switch
                        checked={toolData.staticResponseEnabled}
                        onChange={e =>
                          handleInputChange(
                            'staticResponseEnabled',
                            e.target.checked
                          )
                        }
                        color="primary"
                        size="small"
                        sx={{
                          width: 36,
                          height: 20,
                          padding: 0,
                          '& .MuiSwitch-switchBase': {
                            padding: 0,
                            margin: '2px',
                            transitionDuration: '300ms',
                            '&.Mui-checked': {
                              transform: 'translateX(16px)',
                              color: '#fff',
                              '& + .MuiSwitch-track': {
                                backgroundColor: '#6366F1',
                                opacity: 1,
                                border: 0,
                              },
                            },
                            '&.Mui-focusVisible .MuiSwitch-thumb': {
                              color: '#6366F1',
                              border: '6px solid #fff',
                            },
                          },
                          '& .MuiSwitch-thumb': {
                            boxSizing: 'border-box',
                            width: 16,
                            height: 16,
                          },
                          '& .MuiSwitch-track': {
                            borderRadius: 26 / 2,
                            backgroundColor: '#E9E9EA',
                            opacity: 1,
                          },
                        }}
                      />
                    </Box>
                    {toolData.staticResponseEnabled && (
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={toolData.staticResponse}
                        onChange={e =>
                          handleInputChange('staticResponse', e.target.value)
                        }
                        placeholder="Static response to the tool"
                        sx={{
                          ...commonStyles.textField,
                          mt: 1,
                          '& .MuiOutlinedInput-root': {
                            ...commonStyles.textField[
                              '& .MuiOutlinedInput-root'
                            ],
                            height: 'auto',
                            minHeight: '40px',
                          },
                        }}
                      />
                    )}
                  </Grid2>
                </Grid2>
              </Box>
            </Box>

            {hasAttemptedSave && Object.keys(errors).length > 0 && (
              <Alert
                severity="error"
                sx={{
                  mt: 2,
                  fontSize: 14,
                  borderRadius: '6px',
                }}
              >
                Please fix the errors above before saving.
              </Alert>
            )}
          </Box>
        </Box>
      </Drawer>
      <MessagePopup alert={alert} setAlert={setAlert} />
    </>
  );
};

export default ToolCreator;
